import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { Alert, ButtonGroup, DecisionStamp } from 'common-src/components/base';

import MocaChoice from './MocaChoice';
import {
  formatPersonStamp,
  formatStamp,
  getMocaPromptSource,
  getProviderLabel,
  getTeamLabel,
  IN_PERSON_LABEL,
} from './mocaPrompt';
import styles from './MocaPrompt.module.scss';
import WatchLink from './WatchLink';

const ANSWER_OPTIONS = [
  { value: 'agree', label: 'Agree' },
  { value: 'disagree', label: 'Disagree' },
  { value: 'edit', label: 'Edit' },
];

const OWN_CALL_OPTIONS = [
  { value: 'plan', label: 'Plan in person' },
  { value: 'skip', label: 'Not needed' },
  { value: 'edit', label: 'Edit' },
];

const INSTEAD_OPTIONS = [
  { value: 'plan', label: 'In person' },
  { value: 'skip', label: 'Not needed' },
];

const GhostButton = ({ onClick, disabled, children }) => (
  <button type="button" className={styles.ghostButton} disabled={disabled} onClick={onClick}>
    {children}
  </button>
);

GhostButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

const InsteadChoice = ({ onPick, onCancel, disabled }) => (
  <div className={styles.insteadRow}>
    <span>Instead:</span>
    <ButtonGroup label="Instead" options={INSTEAD_OPTIONS} disabled={disabled} onSelect={onPick} />
    <GhostButton onClick={onCancel}>Cancel</GhostButton>
  </div>
);

InsteadChoice.propTypes = {
  onPick: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

const ReasonRow = ({ disabled, onSave, onCancel }) => {
  const [reason, setReason] = useState('');
  const reasonRef = useRef(null);

  useEffect(() => {
    reasonRef.current?.focus();
  }, []);

  const save = () => {
    if (reason.trim()) onSave(reason.trim());
  };

  return (
    <div className={styles.reasonRow}>
      <input
        ref={reasonRef}
        className={styles.reasonInput}
        value={reason}
        placeholder="Why? (optional)"
        aria-label="Reason for disagreeing"
        onChange={(event) => setReason(event.target.value)}
        onKeyDown={(event) => {
          event.stopPropagation();
          if (event.key === 'Enter') save();
        }}
      />
      <button
        type="button"
        className={styles.noteButton}
        disabled={disabled || !reason.trim()}
        onClick={save}
      >
        Save
      </button>
      <GhostButton onClick={onCancel}>Cancel</GhostButton>
    </div>
  );
};

ReasonRow.propTypes = {
  disabled: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

const ProviderAnswer = ({ moca, past, addingReason, onAddReason, onCloseReason }) => {
  const { provider, outcome } = moca.state;
  const canAddReason = !past && provider.choice === 'disagree' && !provider.reason;

  return (
    <>
      <div className={styles.answerLine}>
        <DecisionStamp
          label={`You: ${getProviderLabel(provider, outcome)}`}
          note={provider.choice === 'disagree' ? provider.reason : undefined}
          stamp={formatPersonStamp(provider)}
          disabled={moca.pending}
          onUndo={past ? undefined : () => moca.undo('provider')}
        />
        {canAddReason && !addingReason && (
          <GhostButton onClick={onAddReason}>Add a reason</GhostButton>
        )}
      </div>
      {canAddReason && addingReason && (
        <ReasonRow
          disabled={moca.pending}
          onSave={(reason) => {
            moca.addReason(reason);
            onCloseReason();
          }}
          onCancel={onCloseReason}
        />
      )}
    </>
  );
};

ProviderAnswer.propTypes = {
  moca: PropTypes.object.isRequired,
  past: PropTypes.bool,
  addingReason: PropTypes.bool.isRequired,
  onAddReason: PropTypes.func.isRequired,
  onCloseReason: PropTypes.func.isRequired,
};

const HudAlert = ({ latestMoca, reviewUrl, moca, past }) => {
  const { team, provider } = moca.state;
  const [choosingInstead, setChoosingInstead] = useState(false);
  const [addingReason, setAddingReason] = useState(false);
  const settled = past || Boolean(provider);
  const title = past ? IN_PERSON_LABEL : `${IN_PERSON_LABEL} today`;

  const answer = (choice, note) => {
    if (choice === 'disagree' && team?.choice === 'edit') {
      setChoosingInstead(true);
      return;
    }
    moca.decide('provider', choice, note);
  };

  let choice = null;
  if (!past && !provider) {
    choice = choosingInstead ? (
      <InsteadChoice
        disabled={moca.pending}
        onPick={(instead) => {
          moca.decide('provider', 'disagree', undefined, instead);
          setChoosingInstead(false);
        }}
        onCancel={() => setChoosingInstead(false)}
      />
    ) : (
      <MocaChoice
        label={team ? 'Your answer' : 'Your call'}
        options={team ? ANSWER_OPTIONS : OWN_CALL_OPTIONS}
        placeholder="What should happen instead?"
        disabled={moca.pending}
        onChoose={answer}
      />
    );
  }

  let teamLine = 'Care team did not decide.';
  if (team) teamLine = `Care team: ${getTeamLabel(team)} · ${formatStamp(team)}`;
  else if (!past)
    teamLine = provider ? 'Care team did not decide.' : 'Care team has not decided yet. Your call.';

  const actions =
    choice || reviewUrl ? (
      <>
        {choice}
        {reviewUrl && !choosingInstead && <WatchLink reviewUrl={reviewUrl} />}
      </>
    ) : null;

  return (
    <Alert variant={settled ? 'default' : 'warning'} title={title} actions={actions}>
      {getMocaPromptSource(latestMoca)}
      <div>{teamLine}</div>
      {provider && (
        <ProviderAnswer
          moca={moca}
          past={past}
          addingReason={addingReason}
          onAddReason={() => setAddingReason(true)}
          onCloseReason={() => setAddingReason(false)}
        />
      )}
    </Alert>
  );
};

HudAlert.propTypes = {
  latestMoca: PropTypes.object,
  reviewUrl: PropTypes.string,
  moca: PropTypes.object.isRequired,
  past: PropTypes.bool,
};

const MocaPromptAlert = (props) => <HudAlert {...props} />;

MocaPromptAlert.propTypes = HudAlert.propTypes;

export default MocaPromptAlert;
